import { Link } from "wouter";
import { motion } from "framer-motion";
import {
  Users,
  Heart,
  Shield,
  AlertTriangle,
  Camera,
  Megaphone,
  Lock,
  Scale,
  ArrowLeft,
  Mail,
  MessageSquare,
  Ban,
  CheckCircle2,
  XCircle,
  HelpCircle
} from "lucide-react";
import { Card } from "@/components/ui/card";

export default function CommunityGuidelines() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/5 via-background to-muted/30">
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <Link href="/">
            <a className="font-serif text-2xl font-semibold tracking-tight" data-testid="link-home">
              Life Itself Studio
            </a>
          </Link>
        </div>
      </header>

      <main className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link href="/">
              <a className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6">
                <ArrowLeft className="h-4 w-4" />
                Back to Home
              </a>
            </Link>

            <div className="flex items-center gap-3 mb-6">
              <Users className="h-8 w-8 text-primary" />
              <h1 className="font-serif text-4xl md:text-5xl font-semibold" data-testid="heading-community-guidelines">
                Community Guidelines
              </h1>
            </div>

            <p className="text-muted-foreground mb-8">
              Last Updated: January 2, 2026
            </p>

            {/* Welcome Section */}
            <Card className="p-6 mb-8 bg-primary/5 border-primary/20">
              <div className="flex items-start gap-4">
                <Heart className="h-6 w-6 text-primary mt-1 flex-shrink-0" />
                <div>
                  <h2 className="font-serif text-xl font-medium mb-2">Welcome to Our Community</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    The Life Itself Studio community is a gathering place for terrarium enthusiasts, plant lovers,
                    and anyone captivated by the magic of miniature ecosystems. Whether you're a seasoned
                    terrarium builder or just discovering the joy of these living works of art, we're thrilled
                    to have you here. These guidelines help ensure our community remains a welcoming, inspiring,
                    and safe space for everyone.
                  </p>
                </div>
              </div>
            </Card>

            <div className="prose prose-slate dark:prose-invert max-w-none space-y-10">
              {/* Core Values Section */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <Heart className="h-6 w-6 text-primary" />
                  <h2 className="font-serif text-2xl font-medium m-0">Our Core Values</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Our community is built on these foundational principles:
                </p>
                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="p-4">
                    <h3 className="font-medium text-lg mb-2">Curiosity & Learning</h3>
                    <p className="text-muted-foreground text-sm">
                      We celebrate questions, experimentation, and the joy of discovering something new.
                      Every expert was once a beginner.
                    </p>
                  </Card>
                  <Card className="p-4">
                    <h3 className="font-medium text-lg mb-2">Kindness & Respect</h3>
                    <p className="text-muted-foreground text-sm">
                      We treat every community member with dignity and compassion, regardless of their
                      experience level or background.
                    </p>
                  </Card>
                  <Card className="p-4">
                    <h3 className="font-medium text-lg mb-2">Authenticity</h3>
                    <p className="text-muted-foreground text-sm">
                      We value genuine connections and honest sharing. Real experiences, including
                      failures, help us all grow together.
                    </p>
                  </Card>
                  <Card className="p-4">
                    <h3 className="font-medium text-lg mb-2">Environmental Stewardship</h3>
                    <p className="text-muted-foreground text-sm">
                      We respect nature and promote sustainable, ethical practices in plant keeping
                      and terrarium building.
                    </p>
                  </Card>
                </div>
              </section>

              {/* Expected Behavior Section */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <CheckCircle2 className="h-6 w-6 text-green-600" />
                  <h2 className="font-serif text-2xl font-medium m-0">Expected Behavior</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  To maintain a positive community atmosphere, we ask that all members:
                </p>
                <ul className="list-none space-y-3 p-0">
                  <li className="flex items-start gap-3 text-muted-foreground">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-foreground">Be welcoming and inclusive</strong> - Make newcomers feel valued and help them get started on their terrarium journey</span>
                  </li>
                  <li className="flex items-start gap-3 text-muted-foreground">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-foreground">Share knowledge generously</strong> - Your experience can help others succeed</span>
                  </li>
                  <li className="flex items-start gap-3 text-muted-foreground">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-foreground">Give constructive feedback</strong> - When offering advice, be helpful and encouraging</span>
                  </li>
                  <li className="flex items-start gap-3 text-muted-foreground">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-foreground">Assume good intentions</strong> - Give others the benefit of the doubt before reacting negatively</span>
                  </li>
                  <li className="flex items-start gap-3 text-muted-foreground">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-foreground">Respect different perspectives</strong> - There are many valid approaches to terrarium building</span>
                  </li>
                  <li className="flex items-start gap-3 text-muted-foreground">
                    <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong className="text-foreground">Report issues, don't escalate</strong> - If you see a problem, use our reporting tools rather than engaging in conflict</span>
                  </li>
                </ul>
              </section>

              {/* Prohibited Content Section */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <XCircle className="h-6 w-6 text-red-600" />
                  <h2 className="font-serif text-2xl font-medium m-0">Prohibited Content & Behavior</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  The following content and behaviors are not permitted in our community:
                </p>

                <div className="space-y-6">
                  <div>
                    <h3 className="font-medium text-lg mb-3 flex items-center gap-2">
                      <Ban className="h-5 w-5 text-red-600" />
                      Hate Speech & Discrimination
                    </h3>
                    <p className="text-muted-foreground text-sm mb-2">
                      Content that attacks, demeans, or discriminates against individuals or groups based on:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1">
                      <li>Race, ethnicity, or national origin</li>
                      <li>Religion or beliefs</li>
                      <li>Gender, gender identity, or sexual orientation</li>
                      <li>Disability or health condition</li>
                      <li>Age or socioeconomic status</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-medium text-lg mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-red-600" />
                      Harassment & Bullying
                    </h3>
                    <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1">
                      <li>Personal attacks, insults, or name-calling</li>
                      <li>Threats of violence or harm (direct or implied)</li>
                      <li>Stalking, doxxing, or sharing private information without consent</li>
                      <li>Repeated unwanted contact after being asked to stop</li>
                      <li>Mocking or ridiculing other members' terrariums or skill level</li>
                      <li>Coordinated attacks against individuals</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-medium text-lg mb-3 flex items-center gap-2">
                      <MessageSquare className="h-5 w-5 text-red-600" />
                      Spam & Disruptive Behavior
                    </h3>
                    <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1">
                      <li>Excessive posting of identical or similar content</li>
                      <li>Off-topic posts designed to derail discussions</li>
                      <li>Automated posting or bot activity</li>
                      <li>Pyramid schemes, scams, or fraudulent offers</li>
                      <li>Unsolicited commercial messages or advertisements</li>
                      <li>Misleading information or clickbait</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-medium text-lg mb-3 flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-orange-600" />
                      Dangerous Plant Advice
                    </h3>
                    <p className="text-muted-foreground text-sm mb-2">
                      Plant safety is paramount. The following is strictly prohibited:
                    </p>
                    <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1">
                      <li>Recommending toxic plants without proper warnings (especially around pets/children)</li>
                      <li>Encouraging collection of wild plants from protected areas</li>
                      <li>Sharing advice about using pesticides or chemicals unsafely</li>
                      <li>Promoting invasive species that could harm local ecosystems</li>
                      <li>Providing medical advice about plant consumption or remedies</li>
                      <li>Encouraging illegal import/export of protected plant species</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="font-medium text-lg mb-3 flex items-center gap-2">
                      <XCircle className="h-5 w-5 text-red-600" />
                      Other Prohibited Content
                    </h3>
                    <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1">
                      <li>Adult, sexually explicit, or obscene content</li>
                      <li>Gore, graphic violence, or disturbing imagery</li>
                      <li>Content promoting illegal activities</li>
                      <li>Malware, phishing, or security threats</li>
                      <li>Impersonation of other community members or staff</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Intellectual Property Section */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <Camera className="h-6 w-6 text-primary" />
                  <h2 className="font-serif text-2xl font-medium m-0">Photo Sharing & Intellectual Property</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We love seeing your beautiful terrariums! Please follow these guidelines when sharing content:
                </p>

                <div className="space-y-4">
                  <Card className="p-4 bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800">
                    <h4 className="font-medium mb-2 text-green-800 dark:text-green-200">Original Content</h4>
                    <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1">
                      <li>Share photos of your own terrariums and plant projects</li>
                      <li>You retain ownership of your original photos and creations</li>
                      <li>By posting, you grant Life Itself Studio a license to feature your content in our community spaces and marketing (with credit)</li>
                    </ul>
                  </Card>

                  <Card className="p-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
                    <h4 className="font-medium mb-2 text-blue-800 dark:text-blue-200">Sharing Others' Work</h4>
                    <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1">
                      <li>Always credit the original creator when sharing inspiration</li>
                      <li>Only share content you have permission to use</li>
                      <li>Link to the original source when possible</li>
                      <li>Ask permission before recreating or closely imitating someone's unique design</li>
                    </ul>
                  </Card>

                  <Card className="p-4 bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800">
                    <h4 className="font-medium mb-2 text-red-800 dark:text-red-200">Prohibited</h4>
                    <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1">
                      <li>Claiming someone else's work as your own</li>
                      <li>Removing watermarks or credits from images</li>
                      <li>Using copyrighted content without permission</li>
                      <li>Sharing photos of private property without consent</li>
                    </ul>
                  </Card>
                </div>
              </section>

              {/* Self-Promotion Section */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <Megaphone className="h-6 w-6 text-primary" />
                  <h2 className="font-serif text-2xl font-medium m-0">Self-Promotion & Commercial Posting</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We want to support our community members' businesses while maintaining a balanced environment:
                </p>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2 text-green-700 dark:text-green-300">Allowed</h4>
                    <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1">
                      <li>Mentioning your business when directly relevant to a discussion</li>
                      <li>Sharing your own terrarium-related products in designated marketplace threads</li>
                      <li>Including a business link in your profile</li>
                      <li>Answering questions that naturally lead to your products/services</li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2 text-red-700 dark:text-red-300">Not Allowed</h4>
                    <ul className="list-disc list-inside text-muted-foreground text-sm space-y-1">
                      <li>Unsolicited direct messages promoting products or services</li>
                      <li>Repeatedly posting promotional content outside designated areas</li>
                      <li>Affiliate links without disclosure</li>
                      <li>Disparaging competitors or their products</li>
                      <li>Creating multiple accounts to promote the same business</li>
                      <li>Fake reviews or testimonials</li>
                    </ul>
                  </div>

                  <Card className="p-4 bg-muted/50">
                    <p className="text-sm text-muted-foreground">
                      <strong>Note:</strong> Commercial members should identify themselves as such. We may offer
                      verified seller badges for active community members who meet our standards.
                    </p>
                  </Card>
                </div>
              </section>

              {/* Privacy Section */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <Lock className="h-6 w-6 text-primary" />
                  <h2 className="font-serif text-2xl font-medium m-0">Privacy Expectations</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Respecting privacy is fundamental to our community:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Personal Information:</strong> Never share another person's private information (address, phone number, email, etc.) without their explicit consent</li>
                  <li><strong>Private Conversations:</strong> Do not screenshot or share private messages publicly without permission</li>
                  <li><strong>Location Privacy:</strong> Be mindful when sharing photos that reveal your home address or location details</li>
                  <li><strong>Children:</strong> Do not share photos of minors without parental consent</li>
                  <li><strong>Data Protection:</strong> See our <Link href="/privacy"><a className="text-primary hover:underline">Privacy Policy</a></Link> for how we protect your information</li>
                </ul>
              </section>

              {/* Conflict Resolution Section */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <Scale className="h-6 w-6 text-primary" />
                  <h2 className="font-serif text-2xl font-medium m-0">Conflict Resolution</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Disagreements happen. Here's how we handle them:
                </p>

                <div className="space-y-4">
                  <Card className="p-4">
                    <h4 className="font-medium mb-2">Step 1: Pause & Reflect</h4>
                    <p className="text-muted-foreground text-sm">
                      Before responding to something that upsets you, take a moment. Consider whether
                      you may have misunderstood, and whether a response is necessary.
                    </p>
                  </Card>

                  <Card className="p-4">
                    <h4 className="font-medium mb-2">Step 2: Direct Communication</h4>
                    <p className="text-muted-foreground text-sm">
                      If appropriate, try to resolve the issue directly and privately with the other party.
                      Be respectful and focus on the specific issue, not personal attacks.
                    </p>
                  </Card>

                  <Card className="p-4">
                    <h4 className="font-medium mb-2">Step 3: Report to Moderators</h4>
                    <p className="text-muted-foreground text-sm">
                      If direct resolution isn't possible or the behavior violates our guidelines,
                      use our reporting tools. Moderators will review the situation impartially.
                    </p>
                  </Card>

                  <Card className="p-4">
                    <h4 className="font-medium mb-2">Step 4: Accept the Outcome</h4>
                    <p className="text-muted-foreground text-sm">
                      Moderator decisions are final for the immediate issue. If you believe a decision
                      was made in error, you may appeal through proper channels (see Appeals Process below).
                    </p>
                  </Card>
                </div>
              </section>

              {/* Moderation & Enforcement Section */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                  <h2 className="font-serif text-2xl font-medium m-0">Moderation & Enforcement</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  We use a graduated response framework to address guideline violations:
                </p>

                <div className="space-y-4">
                  <Card className="p-4 border-l-4 border-l-yellow-500">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-yellow-500 text-white text-sm flex items-center justify-center">1</span>
                      Warning
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      For first-time or minor violations, we'll issue a friendly warning explaining the issue
                      and how to avoid it in the future. Content may be removed.
                    </p>
                  </Card>

                  <Card className="p-4 border-l-4 border-l-orange-500">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-orange-500 text-white text-sm flex items-center justify-center">2</span>
                      Temporary Restriction
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Repeated violations or more serious infractions may result in temporary posting
                      restrictions (24 hours to 30 days) or limited account features.
                    </p>
                  </Card>

                  <Card className="p-4 border-l-4 border-l-red-500">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-red-500 text-white text-sm flex items-center justify-center">3</span>
                      Temporary Ban
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Serious violations or continued patterns of misbehavior may result in a temporary
                      ban from the community (typically 7-90 days).
                    </p>
                  </Card>

                  <Card className="p-4 border-l-4 border-l-red-900">
                    <h4 className="font-medium mb-2 flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-red-900 text-white text-sm flex items-center justify-center">4</span>
                      Permanent Ban
                    </h4>
                    <p className="text-muted-foreground text-sm">
                      Severe violations (threats, hate speech, illegal activity) or repeated serious
                      offenses may result in permanent removal from the community.
                    </p>
                  </Card>
                </div>

                <Card className="p-4 mt-6 bg-muted/50">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <HelpCircle className="h-5 w-5" />
                    Appeals Process
                  </h4>
                  <p className="text-muted-foreground text-sm mb-2">
                    If you believe a moderation decision was made in error:
                  </p>
                  <ol className="list-decimal list-inside text-muted-foreground text-sm space-y-1">
                    <li>Wait 24 hours before submitting an appeal (cooling off period)</li>
                    <li>Email <a href="mailto:community@lifeitself.studio" className="text-primary hover:underline">community@lifeitself.studio</a> with "Appeal" in the subject</li>
                    <li>Include your username, the action taken, and why you believe it was in error</li>
                    <li>Provide any relevant context or evidence</li>
                    <li>Appeals are reviewed within 5 business days by senior moderators</li>
                  </ol>
                  <p className="text-muted-foreground text-sm mt-2">
                    <strong>Note:</strong> Appeals for severe violations (threats, hate speech) are rarely granted.
                  </p>
                </Card>
              </section>

              {/* Reporting Section */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <AlertTriangle className="h-6 w-6 text-primary" />
                  <h2 className="font-serif text-2xl font-medium m-0">How to Report Issues</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  If you see content or behavior that violates these guidelines:
                </p>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-medium">1</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Use the Report Button</h4>
                      <p className="text-muted-foreground text-sm">
                        Every post and comment has a "Report" option. This is the fastest way to alert moderators.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-medium">2</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Email Our Team</h4>
                      <p className="text-muted-foreground text-sm">
                        For urgent matters or complex situations, email{" "}
                        <a href="mailto:community@lifeitself.studio" className="text-primary hover:underline">
                          community@lifeitself.studio
                        </a>
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <span className="text-primary font-medium">3</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Provide Details</h4>
                      <p className="text-muted-foreground text-sm">
                        Include screenshots, links, and context. The more information you provide,
                        the faster we can address the issue.
                      </p>
                    </div>
                  </div>
                </div>

                <Card className="p-4 mt-4 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800">
                  <h4 className="font-medium mb-2 text-blue-800 dark:text-blue-200">Emergency Situations</h4>
                  <p className="text-muted-foreground text-sm">
                    If you believe someone is in immediate danger, please contact local emergency services (911) first.
                    Then notify us so we can take appropriate action on our platform.
                  </p>
                </Card>
              </section>

              {/* Moderator Standards */}
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <Shield className="h-6 w-6 text-primary" />
                  <h2 className="font-serif text-2xl font-medium m-0">Our Moderator Standards</h2>
                </div>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  Our moderators are committed to upholding these principles:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li><strong>Impartiality:</strong> All members are treated equally regardless of their status or history</li>
                  <li><strong>Transparency:</strong> We explain our decisions and reasoning when taking action</li>
                  <li><strong>Consistency:</strong> Similar violations receive similar responses</li>
                  <li><strong>Confidentiality:</strong> Report details and reporter identity are kept private</li>
                  <li><strong>Documentation:</strong> All moderation actions are logged for accountability</li>
                  <li><strong>Accessibility:</strong> Members can always reach out to discuss concerns</li>
                </ul>
              </section>

              {/* Updates Section */}
              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">Updates to These Guidelines</h2>
                <p className="text-muted-foreground leading-relaxed">
                  We may update these community guidelines as our community grows and evolves. Significant
                  changes will be announced through our official channels. We encourage you to review
                  these guidelines periodically.
                </p>
              </section>

              {/* Contact Section */}
              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">Questions?</h2>
                <p className="text-muted-foreground leading-relaxed mb-4">
                  If you have questions about these guidelines or need clarification, we're here to help:
                </p>
                <div className="bg-muted/50 rounded-lg p-6">
                  <p className="font-medium mb-2">Life Itself Studio Community Team</p>
                  <p className="text-muted-foreground flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <a href="mailto:community@lifeitself.studio" className="text-primary hover:underline">
                      community@lifeitself.studio
                    </a>
                  </p>
                </div>
              </section>

              {/* Related Links */}
              <section>
                <h2 className="font-serif text-2xl font-medium mb-4">Related Policies</h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Link href="/privacy">
                    <a className="block p-4 rounded-lg border hover:border-primary transition-colors">
                      <Shield className="h-5 w-5 text-primary mb-2" />
                      <p className="font-medium">Privacy Policy</p>
                      <p className="text-muted-foreground text-sm">How we protect your data</p>
                    </a>
                  </Link>
                  <Link href="/terms">
                    <a className="block p-4 rounded-lg border hover:border-primary transition-colors">
                      <Scale className="h-5 w-5 text-primary mb-2" />
                      <p className="font-medium">Terms of Service</p>
                      <p className="text-muted-foreground text-sm">Our legal agreement</p>
                    </a>
                  </Link>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </main>

      <footer className="py-8 border-t border-border">
        <div className="max-w-7xl mx-auto px-4 text-center text-muted-foreground text-sm">
          <p>&copy; {new Date().getFullYear()} Life Itself Studio. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
